/**
 * Enhanced Psychometric Report Generator
 * Generates detailed psychometric trait analysis reports
 */

export function generateEnhancedPsychometricReport(personalInfo, cognitiveProfile, responses) {
  if (!cognitiveProfile) return null;

  const age = personalInfo.age || 15;
  const sport = personalInfo.sport || 'Athletics';

  // Generate 10 trait analyses
  const traitAnalyses = generatePsychometricTraits(cognitiveProfile, responses, sport);
  
  // Generate mental summary
  const mentalSummary = generateMentalSummary(traitAnalyses);
  
  // Generate action steps
  const actionSteps = generateActionSteps(traitAnalyses, sport);

  return {
    // Header
    studentName: `${personalInfo.firstName} ${personalInfo.lastName}`,
    school: personalInfo.schoolName,
    grade: personalInfo.grade,
    section: personalInfo.section,
    age,
    sport,
    reportType: 'Psychometric Report',

    // 10 Trait Analyses
    traitAnalyses,

    // Mental Summary (5 cards)
    mentalSummary,

    // Next Action Steps (3 cards)
    actionSteps,

    // Get The Edge (motivational banner)
    getTheEdge: {
      icon: '💡',
      message: 'Remember, every setback is a setup for a comeback. Keep pushing forward and harness your strengths!'
    }
  };
}

function generatePsychometricTraits(cognitiveProfile, responses, sport) {
  const traits = [];

  // 1. Stress Response
  traits.push({
    number: 1,
    title: 'Stress Response',
    icon: '🔥',
    yourStyle: cognitiveProfile.profile.stressResponse.type,
    description: 'You are an action-driven extrovert who thrives under pressure, drawing strength from the adrenaline rush. This competitive stress response means you actually perform better when the stakes are high and the crowd is engaged. Unlike moments when you may feel flat or bored, you channel that rush into better performance. In pressure moments, you\'re likely to feel alive and energized rather than overwhelmed. This makes you an ideal fit for high-energy team sports where the competitive advantage is significant.',
    actionTip: {
      icon: '⚡',
      text: 'Embrace the energy of the crowd and channel it into your gameplay! Use pre-game routines that involve your teammates to build collective energy. Practice performing in front of audiences during training to desensitize yourself to the adrenaline rush. In pressure moments, take a deep breath and consciously draw on that rush—use it to sharpen your focus rather than let it overwhelm you. Practice performing in front of audiences during training to desensitize yourself to the adrenaline rush.'
    }
  });

  // 2. Resilience Driver
  traits.push({
    number: 2,
    title: 'Resilience Driver',
    icon: '🎯',
    yourStyle: 'Goal-Oriented',
    description: 'Your goal-oriented mindset helps you bounce back from setbacks by focusing on the next target. When you face obstacles or failures, you don\'t dwell on them—you quickly shift your attention to the next goal. This trait is essential for athletes, allowing you to excel and stay competitive even when things don\'t go as planned. You naturally analyze what went wrong, extract lessons, and refocus on your approach. This trait keeps you competitive and motivated even in the face of setbacks.',
    actionTip: {
      icon: '⚡',
      text: 'Set clear goals after every performance to guide your recovery and growth. Use the 80/20 framework: Specific, Measurable, Achievable, Relevant, Time-bound goals. After each training or match, identify one key area to improve and set a specific goal for it. Keep a goal journal to track progress. When facing setbacks, immediately set a new short-term goal to maintain motivation. When facing setbacks, immediately set a comeback goal that is concrete and achievable within a short timeframe to channel your energy productively.'
    }
  });

  // 3. Learning Style
  traits.push({
    number: 3,
    title: 'Learning Style',
    icon: '📚',
    yourStyle: cognitiveProfile.profile.learningStyle.type,
    description: 'As a detail-oriented learner, you thrive on concrete steps, proven methods, and systematic approaches. You prefer understanding the "how" before the "why," learning best through repetition, clear instructions, and structured practice. This structured learning style means you excel when coaches provide step-by-step guidance and when drills are broken down into manageable components. You appreciate knowing exactly what to do and how to do it.',
    actionTip: {
      icon: '⚡',
      text: 'Break down every training into specific drills to enhance your technical skills. Create a skills checklist for your sport and work through it systematically. Ask your coach for detailed breakdowns of complex movements. Keep a training log with specific notes on technique. Use video analysis to study your form step-by-step. Master fundamentals completely before moving to advanced techniques.'
    }
  });

  // 4. Leadership Style
  traits.push({
    number: 4,
    title: 'Leadership Style',
    icon: '👑',
    yourStyle: 'Supportive & Empathetic',
    description: 'You lead with a supportive, empathetic approach, prioritizing team morale and individual well-being over strict directives. This emotionally intelligent leadership means you\'re attuned to teammates\' feelings and needs. You lead by example, offering encouragement and creating a positive psychological safety where everyone feels heard. You will resonate well with teammates who value collaboration, trust, and mutual support. This style makes you an effective team builder, fostering unity and long-term success.',
    actionTip: {
      icon: '⚡',
      text: 'Regularly check in with teammates to boost morale and cohesion. Make it a habit to acknowledge teammates\' efforts, not just results. Notice when someone is struggling and offer support. In difficult moments, remind the team of shared goals and past successes. Balance being supportive with maintaining standards—use "I" statements to give feedback. Your ability to nurture team unity is a contagious asset—use it to lift the entire team\'s performance and lower dropout rates.'
    }
  });

  // 5. Competitive Mindset
  traits.push({
    number: 5,
    title: 'Competitive Mindset',
    icon: '💪',
    yourStyle: 'Confident Yet Fair',
    description: 'With a confident-yet-fair mindset, you believe in your ability to succeed even against tough competition, viewing challenges as opportunities to prove yourself rather than threats to avoid. This "self-belief" isn\'t arrogance—it\'s a deep-seated confidence in your preparation, abilities, and capacity to perform when it matters most. You approach competition with respect for opponents but never doubt your own potential. This confidence is vital for seating in top international success, as elite competition requires unwavering self-belief. Athletes with strong self-belief tend to perform better on clutch situations and recover faster from losses because they maintain belief in their eventual success.',
    actionTip: {
      icon: '⚡',
      text: 'Channel your confidence into preparation and execution, not just outcome. Develop pre-performance routines that reinforce your self-belief (visualization, affirmations, reviewing past successes). In competition, focus on your process and what you can control. After wins, analyze what you did well. After losses, maintain perspective—one loss doesn\'t define you. Practice positive self-talk: replace "I hope" with "I will." Your confidence should be backed by consistent preparation—this creates unshakeable self-belief.'
    }
  });

  // 6. Discipline Approach
  traits.push({
    number: 6,
    title: 'Discipline Approach',
    icon: '📋',
    yourStyle: 'Structured & Organized',
    description: 'You maintain discipline through structure and organization, preferring routines, schedules, and clear plans. This structured approach means you excel when you have a consistent training schedule, clear goals, and organized systems. You\'re likely to keep training logs, follow meal plans, and stick to sleep schedules. This self-regulation is crucial for long-term athletic development.',
    actionTip: {
      icon: '⚡',
      text: 'Create and maintain structured routines for training, nutrition, and recovery. Use planners or apps to schedule everything: training sessions, meals, sleep, recovery. Build habits through consistency—same time, same place, same routine. When motivation wanes, rely on your systems and routines. Track your adherence to routines and adjust as needed. Your structured approach is a superpower—use it to build unbreakable habits.'
    }
  });

  // 7. Adaptability
  traits.push({
    number: 7,
    title: 'Game Adaptability',
    icon: '🔄',
    yourStyle: 'Quick Adjuster',
    description: 'You adapt quickly to changing situations, making you valuable in dynamic sports environments. When plans change or unexpected situations arise, you adjust your approach without getting flustered. This flexibility allows you to handle in-game adjustments, new strategies, and unpredictable opponents effectively.',
    actionTip: {
      icon: '⚡',
      text: 'Practice scenario-based training to enhance your adaptability. Work with your coach to simulate different game situations and practice adjusting on the fly. Develop multiple strategies for common situations. After games, review moments where you had to adapt and analyze what worked. Your flexibility is an asset—continue developing it through varied training and exposure to different playing styles.'
    }
  });

  // 8. Coach Interaction Style
  traits.push({
    number: 8,
    title: 'Coach Interaction Style',
    icon: '🤝',
    yourStyle: 'Receptive & Collaborative',
    description: 'You interact with coaches in a receptive, collaborative manner, valuing their expertise while also contributing your own insights. You respond well to coaches who explain the "why" behind instructions and who welcome dialogue. This collaborative approach helps you internalize coaching points more effectively.',
    actionTip: {
      icon: '⚡',
      text: 'Maintain open communication with your coach. Ask questions to understand the reasoning behind instructions. Share your observations and feelings about training. Be receptive to feedback—view it as information for improvement, not criticism. Schedule regular check-ins to discuss progress and goals. Your collaborative style strengthens the coach-athlete relationship, leading to better outcomes.'
    }
  });

  // 9. Satisfaction Source
  traits.push({
    number: 9,
    title: 'Satisfaction Source',
    icon: '🌟',
    yourStyle: 'Mastery-Oriented',
    description: 'You derive satisfaction from mastering skills and improving performance, rather than just winning. This mastery orientation means you find joy in the process of getting better, not just the outcome. You\'re motivated by personal bests, technical improvements, and skill development. This trait supports long-term development and resilience.',
    actionTip: {
      icon: '⚡',
      text: 'Focus on process goals alongside outcome goals. Celebrate technical improvements and personal bests, not just wins. Keep a progress journal documenting skill development. Set mastery goals for each training session. When facing setbacks, refocus on what you can learn and improve. Your mastery orientation is ideal for sustained excellence—nurture it by always seeking growth.'
    }
  });

  // 10. Motivation Driver
  traits.push({
    number: 10,
    title: 'Motivation Driver',
    icon: '⚡',
    yourStyle: 'Achievement-Oriented',
    description: 'You\'re driven by achievement and reaching goals. Setting and accomplishing targets fuels your motivation. You thrive when you have clear objectives to work toward and feel energized by progress and success. This achievement orientation makes you goal-focused and results-driven.',
    actionTip: {
      icon: '⚡',
      text: 'Set both short-term and long-term goals to maintain motivation. Use SMART goals (Specific, Measurable, Achievable, Relevant, Time-bound). Break big goals into smaller milestones to create frequent wins. Track and celebrate progress regularly. When motivation dips, revisit your goals and adjust if needed. Your achievement drive is powerful—channel it with clear, compelling goals.'
    }
  });

  return traits;
}

function generateMentalSummary(traitAnalyses) {
  return [
    {
      number: 1,
      title: 'Stress Response',
      yourStyle: 'Action-Driven',
      description: 'You thrive in high-pressure situations, using focused energy to perform at your best.',
      impact: 'This makes you a strategic player when stakes are high, capable of delivering clutch performances.'
    },
    {
      number: 2,
      title: 'Resilience Driver',
      yourStyle: 'Goal-Oriented',
      description: 'You goal-focus helps you bounce back from setbacks by focusing on the next target.',
      impact: 'This trait keeps you competitive and motivated even when facing challenges.'
    },
    {
      number: 3,
      title: 'Learning Style',
      yourStyle: 'Detail-Oriented',
      description: 'You excel with structured, step-by-step learning approaches.',
      impact: 'Your attention to detail ensures thorough skill development and technical mastery.'
    },
    {
      number: 4,
      title: 'Leadership Style',
      yourStyle: 'Supportive',
      description: 'You lead with empathy, fostering team unity and psychological safety.',
      impact: 'This makes you an effective team builder who creates positive, cohesive environments.'
    },
    {
      number: 5,
      title: 'Competitive Mindset',
      yourStyle: 'Confident',
      description: 'You approach competition with self-belief and respect for opponents.',
      impact: 'This mindset not only motivates you but also inspires those around you to strive for excellence.'
    }
  ];
}

function generateActionSteps(traitAnalyses, sport) {
  return [
    {
      icon: '📋',
      title: 'Set Specific Performance Goals',
      description: 'Identify key areas for improvement and create actionable goals to track your progress. Use SMART criteria and review weekly.'
    },
    {
      icon: '🤝',
      title: 'Engage in Team Building Activities',
      description: `Organize sessions that enhance team cohesion and morale, strengthening your leadership skills in ${sport}.`
    },
    {
      icon: '🧘',
      title: 'Practice Visualization Techniques',
      description: 'Incorporate visualization into your training routine to enhance your confidence and focus. Spend 10 minutes daily visualizing successful performances.'
    }
  ];
}
